import { ButtonVariant, CoButton } from "components/CoButton";
import { OpenAccessIcon } from "design_system";
import { LibraryIcon } from "features/libraries";
import useUserSettings from "features/User/hooks/useUserSettings";
import { Paper, PaperDetails } from "helpers/api";
import { addUTMToLink } from "helpers/pageUrl";
import {
  MixpanelActionSource,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import useAnalytics from "hooks/useAnalytics";
import useLabels from "hooks/useLabels";
import { usePdfAccessHierarchy } from "hooks/usePdfAccessHierarchy";
import { ArrowUpRight } from "lucide-react";

type PropsType = {
  item: Paper | PaperDetails;
  className?: string;
  variant?: ButtonVariant;
  actionSource?: MixpanelActionSource;
  index?: number;
  onFullTextClick?: (
    paper: Paper | PaperDetails,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    index?: number,
    libraryId?: string
  ) => void;
};

const FullPaperLibraryLink = ({
  item,
  className = "",
  variant = ButtonVariant.Secondary,
  actionSource,
  index,
  onFullTextClick,
}: PropsType) => {
  const [detailsLabels] = useLabels("screens.details");
  const { clickFullTextLinkEvent, setAnalyticActionSource } = useAnalytics();

  const { library_id } = useUserSettings();

  const { option, url, label, icon } = usePdfAccessHierarchy(
    item,
    detailsLabels["full-text"],
    detailsLabels["pdf"]
  );

  if (!option || !url) {
    return null;
  }

  const handleClick = () => {
    // Track the full text click event
    if (actionSource) {
      setAnalyticActionSource(actionSource);
    }
    const linkType =
      option === "library"
        ? MixpanelFullTextLinkType.LIBRARY
        : option === "open-access"
          ? MixpanelFullTextLinkType.PDF
          : MixpanelFullTextLinkType.DOI;

    if (onFullTextClick && actionSource) {
      onFullTextClick(
        item,
        linkType,
        actionSource,
        index,
        linkType === MixpanelFullTextLinkType.LIBRARY ? library_id : undefined
      );
    } else {
      clickFullTextLinkEvent(linkType); // TODO(Alan): Remove this once we pass thread/search metadata to Details page
    }
    window.open(addUTMToLink(url), "_blank");
  };

  const renderIcon = () => {
    if (option === "doi") {
      return undefined;
    }

    // Handle the library icon case
    if (option === "library") {
      return <LibraryIcon includeTooltip={false} />;
    }

    if (option === "open-access") {
      return <OpenAccessIcon />;
    }

    return icon;
  };

  return (
    <CoButton
      variant={variant}
      size="sm"
      onClick={handleClick}
      label={label}
      icon={renderIcon()}
      iconRight={<ArrowUpRight size={16} />}
      className={className}
    />
  );
};

export default FullPaperLibraryLink;
